import React, { Component } from 'react';
import { OverlayTrigger, Popover } from 'react-bootstrap';
import BzmIcon from 'components/icons/BzmIcon';
import styles from './BzmDateRangePicker.module.css';
import { formatDate, formatDateAndTime } from 'utils/dateUtil';
import clsx from 'clsx';
import ReactDatePickerWrapper from 'views/Analytics/AnalyticsMockService/Reporting/Pickers/BzmDateRangePicker/ReactDatePickerWrapper';
import { FIXED_TIME_RANGE_CHANGED, TIME_RANGE_CHANGED } from 'utils/mixpanelEvents';
import MixpanelService from 'utils/mixpanelService';
import moment from 'moment';

export type FixedTimeRange =
  | 'HOURS_6'
  | 'DAY_1'
  | 'DAY_7'
  | 'DAYS_30'
  | 'ALL_TIME'
  | 'LAST_MONTH'
  | 'LAST_QUARTER'
  | 'LAST_6_MONTHS'
  | 'LAST_YEAR';

type DateRangeButton = {
  label: string;
  optionValue: FixedTimeRange;
};

const dateOptions: DateRangeButton[] = [
  { optionValue: 'HOURS_6', label: '6 Hours' },
  { optionValue: 'DAY_1', label: '24 Hours' },
  { optionValue: 'DAY_7', label: '7 Days' },
  { optionValue: 'DAYS_30', label: '30 Days' },
  { optionValue: 'ALL_TIME', label: 'All Time' },
];

type BzmDateRangePickerStateProps = {
  /** From - To date range. */
  dateRange: DateRange;

  /** Fixed range selection. */
  fixedSelection: FixedTimeRange | null;

  /** Triggered when date range changes */
  dateRangeChanged: (dateRange: DateRange) => void;

  /** Triggered when fixed date selection changes. */
  fixedSelectionChanged?: (fixedDateSelection: FixedTimeRange) => void;

  /** Title above the time picker component */
  title?: string;

  /** Optional override for preset buttons. Defaults to the existing analytics presets. */
  presets?: DateRangeButton[];

  /**
   * Render the preset quick-links inside the calendar popover (below the calendar)
   * instead of as a row below the input. Selecting a preset closes the popover.
   * Defaults to false (legacy below-input layout).
   */
  presetsInPopover?: boolean;

  /**
   * Whether the calendar shows the time-of-day picker. Defaults to true.
   * Set to false for date-only selection (the range applies on second-day click).
   */
  showTimePicker?: boolean;

  /**
   * Render the trigger button at the height of a standard input field (.form-control),
   * with vertically-centered content. Defaults to false (legacy taller layout).
   */
  isCompact?: boolean;

  /** Earliest date selectable in the custom-range calendar. Days before it are locked. */
  minSelectableDate?: Date;
};

type AllProps = BzmDateRangePickerStateProps;

type BzmDateRangePickerState = {
  /** Controlled popover visibility — only used when presetsInPopover is set. */
  show: boolean;
};

export type DateRange = {
  from: Date;
  to: Date;
};

class BzmDateRangePicker extends Component<AllProps, BzmDateRangePickerState> {
  state: BzmDateRangePickerState = { show: false };

  rangeChanged = (from: any, to: any) => {
    MixpanelService.track(TIME_RANGE_CHANGED());
    this.props.dateRangeChanged({ from: from, to: to });
    // With no time picker there is no confirm button, so applying a range from
    // the calendar should also dismiss the (controlled) popover.
    if (this.props.showTimePicker === false) {
      this.setState({ show: false });
    }
  };

  fixedRangeSelectionHandler = (fixedPreselection: FixedTimeRange) => {
    let from: Date = new Date();
    const to: Date = new Date();
    switch (fixedPreselection) {
      case 'HOURS_6':
        from = new Date(new Date().setHours(new Date().getHours() - 6));
        break;

      case 'DAY_1':
        from = new Date(new Date().setDate(new Date().getDate() - 1));
        break;

      case 'DAY_7':
        from = new Date(new Date().setDate(new Date().getDate() - 6));
        from.setHours(0, 0, 0);
        break;

      case 'DAYS_30':
        from = new Date(new Date().setDate(new Date().getDate() - 29));
        from.setHours(0, 0, 0);
        break;

      case 'ALL_TIME':
        from = moment()
          .subtract(6, 'months')
          .startOf('day')
          .toDate();
        break;

      case 'LAST_MONTH':
        from = moment()
          .subtract(1, 'month')
          .startOf('day')
          .toDate();
        break;

      case 'LAST_QUARTER':
        from = moment()
          .subtract(3, 'months')
          .startOf('day')
          .toDate();
        break;

      case 'LAST_6_MONTHS':
        from = moment()
          .subtract(6, 'months')
          .startOf('day')
          .toDate();
        break;

      case 'LAST_YEAR':
        from = moment()
          .subtract(1, 'year')
          .startOf('day')
          .toDate();
        break;
    }

    this.props.dateRangeChanged({ to: to, from: from });
    if (fixedPreselection && this.props.fixedSelectionChanged) {
      MixpanelService.track(FIXED_TIME_RANGE_CHANGED(fixedPreselection));
      this.props.fixedSelectionChanged(fixedPreselection);
    }
  };

  handlePresetClick = (optionValue: FixedTimeRange) => {
    this.fixedRangeSelectionHandler(optionValue);
    // When the presets live inside the popover, applying one should dismiss it.
    if (this.props.presetsInPopover) {
      this.setState({ show: false });
    }
  };

  renderPresets() {
    const activeSelection = styles.activeSelection;
    return (
      <div className={styles.daysPreselection}>
        {(this.props.presets ?? dateOptions).map(item => {
          return (
            <button
              key={item.optionValue}
              onClick={() => this.handlePresetClick(item.optionValue)}
              title={item.label !== 'All Time' ? 'Last ' + item.label : item.label}
              className={clsx('btn btn-link', this.props.fixedSelection === item.optionValue ? activeSelection : '')}
            >
              {item.label}
            </button>
          );
        })}
      </div>
    );
  }

  render() {
    const from = this.props.dateRange.from ? this.props.dateRange.from : new Date();
    const to = this.props.dateRange.to ? this.props.dateRange.to : new Date();
    const presetsInPopover = !!this.props.presetsInPopover;
    const showTimePicker = this.props.showTimePicker !== false;
    const isCompact = !!this.props.isCompact;

    const popover = (
      <Popover id="calendar" style={{ minWidth: '610px' }}>
        <div
          onClick={event => {
            event.preventDefault();
            event.stopPropagation();
          }}
        >
          <ReactDatePickerWrapper
            from={from}
            to={to}
            // TODO - replace with backend call to get real highlighted days
            //highlightedDays={[new Date(2020, 5, 20), new Date(2020, 4, 21)]}
            onRangeChanged={this.rangeChanged}
            showTimePicker={showTimePicker}
            minSelectableDate={this.props.minSelectableDate}
          />
          {presetsInPopover && (
            <div style={{ borderTop: '1px solid #ebeff5', marginTop: 12, padding: '14px 16px 8px' }}>
              {this.renderPresets()}
            </div>
          )}
        </div>
      </Popover>
    );

    // Control the popover when we need to close it programmatically: when presets
    // live inside it, or when there is no time picker confirm button (so a calendar
    // range selection can dismiss it). Otherwise stay uncontrolled (legacy behavior).
    const controlled = presetsInPopover || !showTimePicker;
    const controlledOverlayProps = controlled
      ? { show: this.state.show, onToggle: (next: boolean) => this.setState({ show: next }) }
      : {};

    // Compact mode shows date-only (no time) in the selected interval.
    const formatTrigger = isCompact ? formatDate : formatDateAndTime;

    const DatePickerInput = () => (
      <OverlayTrigger
        transition={false}
        rootClose={true}
        trigger="click"
        placement="bottom"
        overlay={popover}
        {...controlledOverlayProps}
      >
        <button className={clsx(styles.BzmDateRangePicker, isCompact && styles.compact)}>
          <span className={clsx(styles.date, 'text-truncate')} title={from ? ' ' + formatTrigger(from) : '-'}>
            <span>{from ? ' ' + formatTrigger(from) : '-'}</span>
          </span>
          <span className={styles.divider}></span>
          <span className={clsx(styles.date, 'text-truncate')} title={to ? formatTrigger(to) : '-'}>
            <span>{to ? formatTrigger(to) : '-'}</span>
          </span>
          <BzmIcon variant={'icon-bzm-date'} color="var(--primary)" size={20} className={styles.calendarIcon}></BzmIcon>
        </button>
      </OverlayTrigger>
    );

    return (
      <div>
        <label htmlFor="timeRangePicker">{this.props.title ? this.props.title : 'Time Range Selection'}</label>
        <br />
        <DatePickerInput />
        {!presetsInPopover && this.renderPresets()}
      </div>
    );
  }
}

export default BzmDateRangePicker;
